import React, { useEffect, useState } from 'react'
import { Skeleton } from '@chakra-ui/core'
import { ArrowUpRight, Globe } from 'lucide-react'
import { APIResponse, LinkPreview } from 'components/LinkPreview'
import { Button } from 'components/ui/Button'
import useLocaleText from 'components/useLocaleText'
import { linkPopLiterals } from 'components/PopOverview/model/literals'
import styles from './LinkUrlField.module.css'

interface ILinkUrlPreviewProps {
  url: string
}

function PreviewCard({ url, metadata }: ILinkUrlPreviewProps & { metadata: APIResponse | null }) {
  const [failedImage, setFailedImage] = useState<string | null>(null)
  const image = metadata?.image
  const hostname = new URL(url).hostname
  const title = metadata?.title?.trim() || metadata?.siteName?.trim() || url

  return (
    <a className={styles.card} href={url} target="_blank" rel="noopener noreferrer">
      <div className={styles.imagePane}>
        {image && image !== failedImage ? (
          <img src={image} alt="" onError={() => setFailedImage(image)} />
        ) : (
          <Globe size={28} strokeWidth={1.5} aria-hidden="true" />
        )}
      </div>
      <div className={styles.content}>
        <div className={styles.hostname} dir="ltr">
          <Globe size={12} aria-hidden="true" />
          <span>{hostname}</span>
        </div>
        <div className={styles.title}>{title}</div>
        {metadata?.description && <p className={styles.description}>{metadata.description}</p>}
      </div>
      <ArrowUpRight className={styles.openIcon} size={14} aria-hidden="true" />
    </a>
  )
}

export function LinkUrlPreview({ url }: ILinkUrlPreviewProps) {
  const preview = useLocaleText(linkPopLiterals.preview)
  const refresh = useLocaleText(linkPopLiterals.refresh)
  const hidePreview = useLocaleText(linkPopLiterals.hidePreview)
  const showPreview = useLocaleText(linkPopLiterals.showPreview)

  const [settled, setSettled] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const renderPreview = (metadata: APIResponse | null, loading: boolean) => (
    <div className={styles.preview} role="region" aria-label={preview}>
      {hidden ? (
        <Button type="button" variant="ghost" className={styles.showButton} onClick={() => setHidden(false)}>
          {showPreview}
        </Button>
      ) : (
        <>
          <div className={styles.toolbar}>
            <span>{preview}</span>
            <div className={styles.actions}>
              <Button
                type="button"
                variant="ghost"
                className={styles.toolbarButton}
                isDisabled={loading}
                onClick={() => setRefreshKey((value) => value + 1)}
              >
                {refresh}
              </Button>
              <Button type="button" variant="ghost" className={styles.toolbarButton} onClick={() => setHidden(true)}>
                {hidePreview}
              </Button>
            </div>
          </div>
          {loading ? (
            <div className={styles.card} role="status" aria-label={preview} aria-busy="true">
              <div className={`${styles.imagePane} ${styles.skeletonImagePane}`} aria-hidden="true">
                <Skeleton width="100%" height="100%" minHeight="90px" />
              </div>
              <div className={styles.skeletonContent} aria-hidden="true">
                <Skeleton height="11px" width="45%" />
                <Skeleton height="11px" width="100%" />
                <Skeleton height="11px" width="80%" />
                <Skeleton height="11px" width="30%" />
              </div>
            </div>
          ) : (
            <PreviewCard url={url} metadata={metadata} />
          )}
        </>
      )}
    </div>
  )

  useEffect(() => {
    const timeout = setTimeout(() => setSettled(true), 500)
    return () => clearTimeout(timeout)
  }, [url])

  return settled ? (
    <LinkPreview url={url} refreshKey={refreshKey} renderPreview={renderPreview} />
  ) : (
    renderPreview(null, true)
  )
}
